/**
 * Create Scenario page component
 */
import {
    Alert,
    Box,
    Button,
    Snackbar,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { scenariosApi } from '../api/scenarios';
import { useAuth } from '../hooks/useAuth';
import { UserRole } from '../types';
import { SCENARIO_UNITS } from '../types/scenario';

import { GeneralStep } from '../components/Scenarios/GeneralStep';
import { ScenarioModelStep } from '../components/Scenarios/ScenarioModelStep';
import type { MlMode } from '@/types/analogueMl';
import ScenarioEconomicsStep from '../components/Scenarios/ScenarioEconomicsStep';
import { ScenarioLayersStep } from '../components/Scenarios/ScenarioLayersStep';
import { ScenarioEconomicResultsStep } from '../components/Scenarios/ScenarioEconomicResultsStep';
import { ScenarioCalculationsStep } from '../components/Scenarios/ScenarioCalculationsStep';
import {
    getScenarioStepLabel,
    ScenarioStep,
    ScenarioStepsSidebar,
    STEPS_SIDEBAR_COLLAPSED_WIDTH,
    STEPS_SIDEBAR_WIDTH,
} from '../components/Scenarios/ScenarioStepsSidebar';
import { useScenarioWorkflowMobile } from '@/contexts/ScenarioWorkflowMobileContext';
import { useRegisterPageHelp } from '@/hooks/useRegisterPageHelp';
import { ScenarioWellsStep } from '../components/Scenarios/ScenarioWellsStep';
import { EconomicsFormData } from '../types/scenarioEconomics';

interface CreateScenarioPageProps {
  mainSidebarCollapsed?: boolean;
}

// Visibility status UUIDs
const PUBLIC_STATUS_UUID = '53184dbd-ff42-497e-8137-a89dda5e5f87';
const COMPANY_PUBLIC_UUID = '20a7f07b-93c4-4188-93c1-920bbc8796c8';
const PRIVATE_UUID = '8e3819e8-4fb5-4eef-9bcb-188e9f7059e9';

export const CreateScenarioPage: React.FC<CreateScenarioPageProps> = ({
  mainSidebarCollapsed = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const scenarioMode = 'ml' as const;
  const { user } = useAuth();
  const isSuperAdmin = user?.role === UserRole.ADMIN || user?.role === 'admin';
  const [currentStep, setCurrentStep] = useState<ScenarioStep>('general');
  useRegisterPageHelp('Create scenario', getScenarioStepLabel(currentStep, false));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [stepsDrawerOpen, setStepsDrawerOpen] = useState(false);
  const { register: registerScenarioMobileNav } = useScenarioWorkflowMobile();
  const openStepsDrawer = useCallback(() => setStepsDrawerOpen(true), []);

  // Scenario data state
  const [scenarioData, setScenarioData] = useState({
    fieldType: 'new' as 'new' | 'existing',
    fieldId: null as string | null,
    name: '',
    description: '',
    visibility: 'private' as 'public' | 'private',
    visibility_status_id: PRIVATE_UUID,
    unitId: SCENARIO_UNITS.TONS as number,
    coordinate: null as string | null,
    ml_mode: 'default' as MlMode,
  });
  const [modelStepReady, setModelStepReady] = useState(true);

  // Reservoirs state - will be used for scenario creation
  const [layers, setLayers] = useState<any[]>([]);
  const [selectedLayerIds, setSelectedLayerIds] = useState<string[]>([]);

  // Wells state - will be used for scenario creation
  const [wells, setWells] = useState<any[]>([]);

  // Economics state - will be used for scenario creation
  const [economicsData, setEconomicsData] = useState<EconomicsFormData>({});

  // Scenario saving state
  const [savedScenarioId, setSavedScenarioId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isMobile) {
      registerScenarioMobileNav(null);
      return;
    }
    registerScenarioMobileNav({
      onOpen: openStepsDrawer,
      title: getScenarioStepLabel(currentStep, false),
      readOnly: false,
    });
    return () => registerScenarioMobileNav(null);
  }, [isMobile, currentStep, registerScenarioMobileNav, openStepsDrawer]);

  const handleStepChange = async (step: ScenarioStep) => {
    setCurrentStep(step);

    // If we're switching to layers step and scenario is not saved yet, save it first
    if (step === 'layers' && !savedScenarioId) {
      console.log('Switching to layers step, saving scenario first...');
      await saveScenarioDraft();
    }
  };

  const handleCancel = () => {
    // Reset scenario data when canceling
    setScenarioData({
      fieldType: 'new',
      fieldId: null,
      name: '',
      description: '',
      visibility: 'private',
      visibility_status_id: PRIVATE_UUID,
      unitId: SCENARIO_UNITS.TONS,
      coordinate: null,
      ml_mode: 'default',
    });
    navigate('/app/scenarios');
  };

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleFieldTypeChange = useCallback((fieldType: 'new' | 'existing') => {
    setScenarioData(prev => ({ ...prev, fieldType, fieldId: fieldType === 'new' ? null : prev.fieldId }));
  }, []);

  const handleFieldChange = useCallback((fieldId: string | null) => {
    setScenarioData(prev => ({ ...prev, fieldId }));
  }, []);

  const handleNameChange = useCallback((name: string) => {
    setScenarioData(prev => ({ ...prev, name }));
  }, []);

  const handleDescriptionChange = useCallback((description: string) => {
    setScenarioData(prev => ({ ...prev, description }));
  }, []);

  const handleVisibilityChange = useCallback((visibility: 'public' | 'private' | 'company_public') => {
    let visibility_status_id = PRIVATE_UUID;
    if (visibility === 'public') {
      visibility_status_id = PUBLIC_STATUS_UUID;
    } else if (visibility === 'company_public') {
      visibility_status_id = COMPANY_PUBLIC_UUID;
    }
    setScenarioData(prev => ({
      ...prev,
      visibility: visibility === 'company_public' ? 'private' : visibility,  // Keep backward compatibility
      visibility_status_id
    }));
  }, []);

  const handleUnitIdChange = useCallback((unitId: number) => {
    setScenarioData(prev => ({ ...prev, unitId }));
  }, []);

  const handleCoordinateChange = useCallback((coordinate: string | null) => {
    setScenarioData(prev => ({ ...prev, coordinate }));
  }, []);

  const handleMlModeChange = useCallback((ml_mode: MlMode) => {
    setScenarioData(prev => ({ ...prev, ml_mode }));
  }, []);

  const handleModelReadyChange = useCallback((ready: boolean) => {
    setModelStepReady(ready);
  }, []);

  const handleLayersChange = useCallback((newLayers: any[]) => {
    setLayers(newLayers || []);
    console.log('Scenario layers updated:', newLayers, 'Total layers:', newLayers?.length || 0); // For debugging
  }, []);

  const handleWellsChange = useCallback((newWells: any[]) => {
    setWells(newWells || []);
    console.log('Scenario wells updated:', newWells, 'Total wells:', newWells?.length || 0); // For debugging
  }, []);

  const handleEconomicsChange = useCallback((newEconomicsData: EconomicsFormData) => {
    console.log('Scenario economics updated:', newEconomicsData);
    setEconomicsData(newEconomicsData);
  }, []);

  const handleSaveAndGoToResults = useCallback(() => {
    setCurrentStep('calculations');
  }, []);

  const saveScenarioDraft = useCallback(async () => {
    if (!scenarioData.name.trim()) {
      setError('Scenario name is required');
      return null;
    }

    try {
      setSaving(true);
      setError(null);

      const scenarioDataToSave: any = {
        name: scenarioData.name,
        description: scenarioData.description || undefined,
        coordinate: scenarioData.coordinate || null,
        field_type: 'oil', // Default field type, can be made configurable later
        is_public: scenarioData.visibility_status_id === PUBLIC_STATUS_UUID,  // Keep for backward compatibility
        visibility_status_id: scenarioData.visibility_status_id,
        is_draft: true, // Always save as draft initially
        to_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // Default to 1 year from now
        unit_id: scenarioData.unitId ?? SCENARIO_UNITS.TONS,
        mode: 'ml',
        ml_mode: scenarioData.ml_mode ?? 'default',
      };

      // Only include field_id if user selected an existing field
      if (scenarioData.fieldType === 'existing' && scenarioData.fieldId) {
        scenarioDataToSave.field_id = scenarioData.fieldId;
      }

      const response = await scenariosApi.createScenario(scenarioDataToSave);
      setSavedScenarioId(response.id);
      setSuccessMessage('Scenario draft saved successfully!');
      return response.id;
    } catch (err: any) {
      console.error('Failed to save scenario draft:', err);
      setError(err.response?.data?.detail || 'Failed to save scenario draft');
      return null;
    } finally {
      setSaving(false);
    }
  }, [scenarioData]);

  const handleSaveAndContinue = useCallback(async () => {
    const scenarioId = await saveScenarioDraft();
    if (scenarioId) {
      setCurrentStep('layers');
    }
  }, [saveScenarioDraft]);

  const handleGoToWells = useCallback(() => {
    setCurrentStep('model');
  }, []);

  const handleGoToWellsFromModel = useCallback(() => {
    if (!modelStepReady) {
      setError('Finish model configuration before continuing to Reservoir configuration.');
      return;
    }
    setCurrentStep('wells');
  }, [modelStepReady]);

  const handleGoToEconomy = useCallback(() => {
    setCurrentStep('economy');
  }, []);

  const getStepContent = useCallback(() => {
    switch (currentStep) {
      case 'general':
        return (
          <GeneralStep
            scenarioId={savedScenarioId}
            scenarioMode={scenarioMode}
            onEnsureScenarioSaved={saveScenarioDraft}
            onFieldTypeChange={handleFieldTypeChange}
            onFieldChange={handleFieldChange}
            onNameChange={handleNameChange}
            onDescriptionChange={handleDescriptionChange}
            onVisibilityChange={handleVisibilityChange}
            onUnitIdChange={handleUnitIdChange}
            onCoordinateChange={handleCoordinateChange}
            initialData={scenarioData}
          />
        );
      case 'layers':
        return (
          <ScenarioLayersStep
            scenarioId={savedScenarioId || ''}
            onLayersChange={handleLayersChange}
            onGoToWells={handleGoToWells}
            nextLabel="Save and go to Model"
          />
        );
      case 'model':
        return savedScenarioId ? (
          <ScenarioModelStep
            scenarioId={savedScenarioId}
            coordinate={scenarioData.coordinate}
            onMlModeChange={handleMlModeChange}
            onModelReadyChange={handleModelReadyChange}
            onContinue={handleGoToWellsFromModel}
          />
        ) : (
          <Alert severity="info">Save the scenario on General before configuring the model.</Alert>
        );
      case 'wells':
        return (
          <ScenarioWellsStep
            scenarioId={savedScenarioId || ''}
            onWellsChange={handleWellsChange}
            onGoToEconomy={handleGoToEconomy}
          />
        );
      case 'economy':
        return (
          <ScenarioEconomicsStep
            key={`economy-${savedScenarioId}-${currentStep}`}
            scenarioId={savedScenarioId || ''}
            onEconomicsChange={handleEconomicsChange}
            initialData={economicsData}
            onSaveAndGoToResults={handleSaveAndGoToResults}
          />
        );
      case 'calculations':
        return (
          <ScenarioCalculationsStep
            scenarioId={savedScenarioId || ''}
            onCalculationComplete={() => {}}
          />
        );
      case 'results':
        return (
          <ScenarioEconomicResultsStep scenarioId={savedScenarioId || ''} />
        );
      default:
        return 'Select a step to continue';
    }
  }, [currentStep, scenarioData, handleFieldTypeChange, handleFieldChange, handleNameChange, handleDescriptionChange, handleVisibilityChange, handleUnitIdChange, handleCoordinateChange, handleMlModeChange, handleModelReadyChange, handleLayersChange, handleWellsChange, handleEconomicsChange, handleSaveAndGoToResults, handleGoToWells, handleGoToWellsFromModel, handleGoToEconomy, savedScenarioId, economicsData, saveScenarioDraft]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          flexDirection: 'row',
          width: '100%',
        }}
      >
        {!isMobile && (
          <Box
            component="nav"
            sx={{
              width: sidebarCollapsed ? STEPS_SIDEBAR_COLLAPSED_WIDTH : STEPS_SIDEBAR_WIDTH,
              maxWidth: sidebarCollapsed ? STEPS_SIDEBAR_COLLAPSED_WIDTH : STEPS_SIDEBAR_WIDTH,
              flexShrink: 0,
              overflow: 'hidden',
              transition: 'width 0.3s ease',
            }}
          >
            <ScenarioStepsSidebar
              currentStep={currentStep}
              onStepChange={handleStepChange}
              collapsed={sidebarCollapsed}
              onToggleCollapse={handleToggleSidebar}
              mainSidebarCollapsed={mainSidebarCollapsed}
              scenarioMode={scenarioMode}
            />
          </Box>
        )}

        {isMobile && (
          <ScenarioStepsSidebar
            variant="temporary"
            open={stepsDrawerOpen}
            onClose={() => setStepsDrawerOpen(false)}
            currentStep={currentStep}
            onStepChange={handleStepChange}
            collapsed={false}
            scenarioMode={scenarioMode}
          />
        )}

        {/* Main Content Area */}
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            p: { xs: 2, sm: 3 },
            overflow: 'auto',
            boxSizing: 'border-box',
          }}
        >
        {/* Header */}
        <Box sx={{
          mb: 3,
        }}>
          <Typography variant="h4" gutterBottom>
            {savedScenarioId ? 'Edit Scenario' : 'Create New Scenario'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {savedScenarioId
              ? `Editing scenario draft (ID: ${savedScenarioId})`
              : 'Create a new oil and gas field development scenario'
            }
          </Typography>
        </Box>

        {/* Step Content */}
        <Box sx={{
          minHeight: 'calc(100vh - 200px)',
        }}>
          {getStepContent()}

          {/* Action Buttons */}
          {currentStep === 'general' && (
            <Box sx={{
              mt: 4,
              px: 2,
              display: 'flex',
              gap: 2,
            }}>
              <Button
                variant="contained"
                onClick={handleSaveAndContinue}
                disabled={!scenarioData.name.trim() || saving}
                sx={{
                  minWidth: 200,
                }}
              >
                {saving ? 'Saving...' : 'Save and go to reservoirs'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleCancel}
                sx={{
                  minWidth: 100,
                }}
              >
                Cancel
              </Button>
            </Box>
          )}
        </Box>

        </Box>
      </Box>

      {/* Error and Success Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
